import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CircleCheck, CircleAlert, ExternalLink } from "lucide-react";

export default function HostEarnings() {
  const [status, setStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/host/stripe/status").then(r => setStatus(r.data)).catch(() => setStatus({ connected: false, charges_enabled: false, payouts_enabled: false }));
    api.get("/host/earnings").then(r => setEarnings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const onboard = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/host/stripe/onboard", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not start Stripe onboarding";
      if (detail.includes("signed up for Connect") || detail.includes("Connect not available")) {
        setErr("Stripe Connect isn't activated on the Parkpot platform account yet. Once the platform admin enables Connect in the Stripe Dashboard, hosts will be able to onboard here. In the meantime, bookings still process — payouts happen manually.");
      } else {
        setErr(detail);
      }
      setBusy(false);
    }
  };

  const ready = status?.charges_enabled && status?.payouts_enabled;

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-8 py-10">
      <h1 className="font-display text-3xl font-semibold text-[var(--fpn-navy)]" data-testid="earnings-title">Earnings & Payouts</h1>

      <div className="mt-8 border border-slate-200 rounded-2xl p-6 bg-white" data-testid="stripe-status-card">
        <div className="flex items-center gap-3">
          {ready ? <CircleCheck className="text-emerald-600"/> : <CircleAlert className="text-amber-500"/>}
          <div>
            <div className="font-display text-lg font-semibold text-[var(--fpn-navy)]">
              {ready ? "You're ready to receive payouts" : status?.connected ? "Complete your Stripe onboarding" : "Connect your payout account"}
            </div>
            <div className="text-sm text-slate-500">
              {ready ? "Payments from confirmed bookings will land in your bank." : "Parkpot uses Stripe Connect for secure payouts. Onboarding takes a few minutes."}
            </div>
          </div>
        </div>
        {!ready && (
          <button onClick={onboard} disabled={busy} className="btn-amber inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm mt-6" data-testid="stripe-onboard-button">
            {busy ? "Redirecting…" : (<>Start Stripe onboarding <ExternalLink size={14}/></>)}
          </button>
        )}
        {err && <div className="text-sm text-red-600 mt-3" data-testid="stripe-onboard-error">{err}</div>}
        {status?.connected && (
          <div className="mt-4 text-xs text-slate-500 space-y-1">
            <div>Account: <span className="font-mono">{status.account_id}</span></div>
            <div>Charges enabled: {String(!!status.charges_enabled)} · Payouts enabled: {String(!!status.payouts_enabled)}</div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        <Stat testId="earnings-available" label="Available earnings" value={`£${(earnings?.available ?? 0).toFixed(2)}`}/>
        <Stat testId="earnings-pending" label="Pending" value={`£${(earnings?.pending ?? 0).toFixed(2)}`}/>
        <Stat testId="earnings-paid" label="Paid out" value={`£${(earnings?.paid_out ?? 0).toFixed(2)}`}/>
      </div>

      <h2 className="font-display text-xl font-semibold text-[var(--fpn-navy)] mt-12">Recent bookings</h2>
      <div className="mt-4 space-y-2">
        {(earnings?.recent || []).length === 0 && <div className="text-sm text-slate-500 p-6 border border-dashed border-slate-200 rounded-2xl">No paid bookings yet.</div>}
        {(earnings?.recent || []).map(b => (
          <div key={b.id} className="border border-slate-200 rounded-2xl p-4 bg-white flex justify-between items-center flex-wrap gap-3" data-testid={`earning-row-${b.reference}`}>
            <div>
              <div className="font-semibold text-[var(--fpn-navy)]">{b.parking_name}</div>
              <div className="text-xs text-slate-500 font-mono">{b.reference}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-lg font-bold text-[var(--fpn-navy)]">£{(b.host_earnings || 0).toFixed(2)}</div>
              <div className="text-xs text-slate-500">of £{(b.total_price || 0).toFixed(2)} gross</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId} className="border border-slate-200 rounded-2xl p-5 bg-white">
    <div className="text-xs text-slate-500 uppercase tracking-widest">{label}</div>
    <div className="font-display text-3xl font-bold text-[var(--fpn-navy)] mt-2">{value}</div>
  </div>
);
